import { Input } from '@builder.io/sdk';

import { aspectRatio } from '../parts/aspectRatio';
import { videoSettings } from '../parts/videoSettings';
import { gravity } from '../parts/gravity';
import { overlay } from '../parts/overlay';
import { fit } from '../parts/fit';
import { overlayColor } from '../parts/overlayColor';

/**
 * The hover animation style for the media item.
 */
export enum HoverStyle {
  Grow = 'grow',
  OpacityGrow = 'opacity-grow',
  None = 'none'
}

export const defaultMediaValues = {
  aspectRatio: '1:1',
  videoSettings: { autoplay: false, loop: true, muted: true, controls: true },
  gravity: 'auto',
  hoverStyle: HoverStyle.None,
  overlay: false,
  overlayColor: 'inherit'
};

export const mediaSubfields = [
  {
    name: 'defaultMedia',
    type: 'object',
    defaultValue: {
      aspectRatio: '1:1',
      videoSettings: defaultMediaValues.videoSettings,
      featured: false,
      gravity: 'auto',
      hoverStyle: defaultMediaValues.hoverStyle,
      overlay: false,
      overlayColor: 'inherit'
    },
    subFields: [
      { name: 'item', type: 'cloudinaryImageEditor' },
      aspectRatio,
      gravity,
      {
        name: 'hoverStyle',
        type: 'text',
        enum: Object.values(HoverStyle).map((value) => {
          return { label: value.toUpperCase(), value };
        }),
        defaultValue: HoverStyle.None,
        advanced: true,
        helperText: 'The hover animation style for the media item.'
      },
      videoSettings,
      {
        name: 'featured',
        type: 'boolean',
        defaultValue: false,
        description: 'This increases the quality of the image or video.',
        advanced: true
      },
      overlay,
      overlayColor,
      fit,
      {
        name: 'containerHeight',
        type: 'text',
        defaultValue: '100%',
        helperText:
          'The container height of the media item. Accepts any CSS length. E.g. 100%, 100vh, 100px, etc.'
      }
    ]
  },
  {
    name: 'tabletMedia',
    type: 'object',
    defaultValue: {
      aspectRatio: '1:1',
      videoSettings: defaultMediaValues.videoSettings,
      featured: false,
      gravity: 'auto',
      overlay: false,
      overlayColor: 'inherit'
    },
    hoverStyle: defaultMediaValues.hoverStyle,
    subFields: [
      { name: 'item', type: 'cloudinaryImageEditor' },
      aspectRatio,
      gravity,
      {
        name: 'hoverStyle',
        type: 'text',
        enum: Object.values(HoverStyle).map((value) => {
          return { label: value.toUpperCase(), value };
        }),
        defaultValue: HoverStyle.None,
        advanced: true,
        helperText: 'The hover animation style for the media item.'
      },
      videoSettings,
      {
        name: 'featured',
        type: 'boolean',
        defaultValue: false,
        description: 'This increases the quality of the image or video.',
        advanced: true
      },
      overlay,
      overlayColor,
      fit,
      {
        name: 'containerHeight',
        type: 'text',
        defaultValue: '100%',
        helperText:
          'The container height of the media item. Accepts any CSS length. E.g. 100%, 100vh, 100px, etc.'
      }
    ]
  },
  {
    name: 'desktopMedia',
    type: 'object',
    defaultValue: {
      aspectRatio: '1:1',
      videoSettings: defaultMediaValues.videoSettings,
      featured: false,
      gravity: 'auto',
      overlay: false,
      overlayColor: 'inherit'
    },
    hoverStyle: defaultMediaValues.hoverStyle,
    subFields: [
      { name: 'item', type: 'cloudinaryImageEditor' },
      aspectRatio,
      gravity,
      {
        name: 'hoverStyle',
        type: 'text',
        enum: Object.values(HoverStyle).map((value) => {
          return { label: value.toUpperCase(), value };
        }),
        defaultValue: HoverStyle.None,
        advanced: true,
        helperText: 'The hover animation style for the media item.'
      },
      videoSettings,
      {
        name: 'featured',
        type: 'boolean',
        defaultValue: false,
        description: 'This increases the quality of the image or video.',
        advanced: true
      },
      overlay,
      overlayColor,
      fit,
      {
        name: 'containerHeight',
        type: 'text',
        defaultValue: '100%',
        helperText:
          'The container height of the media item. Accepts any CSS length. E.g. 100%, 100vh, 100px, etc.'
      }
    ]
  },
  {
    name: 'highPriority',
    type: 'boolean',
    defaultValue: false,
    helperText:
      'Whether the media should be loaded with high priority. This is useful for images that are critical to the page.'
  }
] as Array<Input>;

export const allDefaultMediaValues = {
  defaultMedia: defaultMediaValues,
  tabletMedia: defaultMediaValues,
  desktopMedia: defaultMediaValues,
  highPriority: false
};

export const media = {
  name: 'media',
  type: 'object',
  defaultValue: allDefaultMediaValues,
  subFields: mediaSubfields
} satisfies Input;
